export interface MermaidPoint {
  x: number;
  y: number;
}

export interface MermaidSize {
  height: number;
  width: number;
}

export interface MermaidTransform extends MermaidPoint {
  scale: number;
}

export interface MermaidScaleBounds {
  max: number;
  min: number;
}

export const MERMAID_MIN_SCALE = 0.25;
export const MERMAID_MAX_SCALE = 4;

export function mermaidScaleBounds(fitScale: number): MermaidScaleBounds {
  return {
    min: Math.min(MERMAID_MIN_SCALE, fitScale),
    max: Math.max(MERMAID_MAX_SCALE, fitScale),
  };
}

export function fitMermaidTransform(viewport: MermaidSize, content: MermaidSize): MermaidTransform {
  const scale = Math.min(viewport.width / content.width, viewport.height / content.height);
  return {
    scale,
    x: (viewport.width - content.width * scale) / 2,
    y: (viewport.height - content.height * scale) / 2,
  };
}

export function zoomMermaidTransform(
  transform: MermaidTransform,
  requestedScale: number,
  anchor: MermaidPoint,
  bounds: MermaidScaleBounds,
): MermaidTransform {
  const scale = Math.min(bounds.max, Math.max(bounds.min, requestedScale));
  const ratio = scale / transform.scale;
  return {
    scale,
    x: anchor.x - (anchor.x - transform.x) * ratio,
    y: anchor.y - (anchor.y - transform.y) * ratio,
  };
}

export function panMermaidTransform(
  transform: MermaidTransform,
  delta: MermaidPoint,
): MermaidTransform {
  return {
    ...transform,
    x: transform.x + delta.x,
    y: transform.y + delta.y,
  };
}

export function pinchMermaidTransform(
  transform: MermaidTransform,
  previous: readonly [MermaidPoint, MermaidPoint],
  next: readonly [MermaidPoint, MermaidPoint],
  bounds: MermaidScaleBounds,
): MermaidTransform {
  const previousMidpoint = midpoint(previous[0], previous[1]);
  const nextMidpoint = midpoint(next[0], next[1]);
  const previousDistance = distance(previous[0], previous[1]);
  const nextDistance = distance(next[0], next[1]);
  if (previousDistance === 0 || nextDistance === 0) return transform;

  const zoomed = zoomMermaidTransform(
    transform,
    transform.scale * (nextDistance / previousDistance),
    previousMidpoint,
    bounds,
  );
  return panMermaidTransform(zoomed, {
    x: nextMidpoint.x - previousMidpoint.x,
    y: nextMidpoint.y - previousMidpoint.y,
  });
}

export function mermaidViewBoxSize(svg: string): MermaidSize | null {
  const match = svg.match(/\bviewBox\s*=\s*["']\s*([^"']+?)\s*["']/i);
  if (!match?.[1]) return null;
  const values = match[1]
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  const width = values[2];
  const height = values[3];
  if (
    values.length !== 4 ||
    width == null ||
    height == null ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }
  return { width, height };
}

function midpoint(a: MermaidPoint, b: MermaidPoint): MermaidPoint {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

function distance(a: MermaidPoint, b: MermaidPoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}
